import React, { useMemo } from 'react';
import cx from 'classnames';
import { useTheme } from '@unisporkal/themes';
import { useTranslations } from '@unisporkal/localization';
import {
  Button,
  ButtonVariantEnum,
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import { ga4Track, toggleInteraction, ctaInteraction } from '@unisporkal/ga4';
import styles from './UltraPack.module.scss';
import Price from './components/Price/Price';
import PerDownload from './components/PerDownload/PerDownload';
import useOnLoad from '../../../../../hooks/queries/useOnLoad';
import { useAcquisitionType } from '../../../../../hooks/stateful/useAcquisitionType';
import useSelectedIndex from '../../../../../hooks/stateful/useSelectedIndex';
import acquisitionTypes from '../../../../../support/acquisitionTypes';

interface UltraPackProps {
  onClick: () => void;
  isSelected: boolean;
}

const findUltraPackData = (config: any) => {
  const data = config.options?.find(
    (option) => option.type === 'standardlicense'
  );
  return data?.sizes?.find((size) => size.license === 'ultrapack');
};

const UltraPack = ({ onClick, isSelected }: UltraPackProps) => {
  const t = useTranslations();
  const { pushAcquisitionView } = useAcquisitionType();
  const { selectedOption, setSelectedSizeIndex } = useSelectedIndex();

  const theme = useTheme();
  const {
    response: { config },
  } = useOnLoad();
  const themeRadio = theme.radio || {};

  if (!config) {
    return null;
  }

  const ultraPackData = useMemo(() => findUltraPackData(config), [config]);

  const ultraPack = useMemo(() => {
    const [pack] = ultraPackData?.imagePackSavings || [];
    return pack;
  }, [ultraPackData]);

  const handleClick = () => {
    onClick();

    const ga4Event = toggleInteraction({
      toggle_interaction: 'rf_toggle',
      toggle_selection: 'alc_up_toggle',
      ui_element_location: 'adp_size_buy_card',
    });

    ga4Track(ga4Event);
  };

  const handlePackSizesClick = () => {
    let sizeIndex = -1;
    ['Large', '4K', 'HD'].forEach((teeShirtSize) => {
      if (sizeIndex > -1) {
        return;
      }

      sizeIndex = (selectedOption?.sizes ?? []).findIndex(
        (size) => size.teeShirtSize === teeShirtSize
      );
    });

    if (sizeIndex > -1) {
      setSelectedSizeIndex(sizeIndex);
    }

    pushAcquisitionView(acquisitionTypes.ULTRA_PACK_UPSELL);

    const ga4Event = ctaInteraction({
      cta_selection: undefined,
      cta_text: 'see_all_pack_sizes',
      cta_url: window.location.pathname,
      ui_element_location: 'adp_size_buy_card',
    });

    ga4Track(ga4Event);
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.itemDetails}
        data-testid="license-size"
      >
        <input
          className={cx(themeRadio.radioButton, styles.radio)}
          checked={isSelected}
          type="radio"
          onChange={handleClick}
        />
        <div className={styles.description}>
          <Typography
            variant={TypographyVariant.body.medium}
            component={TypographyComponentEnum.DIV}
            className={styles.name}
          >
            {t('5_large_images_or_videos')}
          </Typography>
          <Button
            variant={ButtonVariantEnum.TEXT}
            className={styles.packSizes}
            onClick={handlePackSizesClick}
          >
            {t('see_all_pack_sizes')}
          </Button>
        </div>
      </div>
      <div className={styles.price}>
        <Price
          amount={ultraPack.pricePerDownload}
          currencyCode={ultraPackData?.currencyCode || 'USD'}
        />
        <PerDownload />
      </div>
    </div>
  );
};

export default UltraPack;
